// Desktop timer bar (WI-11828).
//
// Shows a Start button when no timer is running, and a compact running card
// (customer / project / activity + elapsed + Stop / Switch) when one is. The
// state is shared with the mobile timer flow via the localStorage hook in
// lib/timer.ts, so resizing the browser between phone and desktop never loses
// the running task.

import { useEffect, useState, useCallback } from 'react'
import { useQuery } from '@tanstack/react-query'
import { Play, Square, RefreshCcw, Clock } from 'lucide-react'
import { api } from '@/lib/api/client'
import type { WorkTreeCustomer } from '@/types/api'
import {
  type TaskRef,
  useTimerState, useTimerActions, fmtDur,
} from '@/lib/timer'
import { PickerSheet } from '@/modules/time/MobileTimePage'

export default function DesktopTimerBar() {
  const [state, setState] = useTimerState()
  const [tick, setTick]   = useState(Date.now())
  const [pickerOpen, setPickerOpen] = useState(false)
  const { startTask, stopRunning } = useTimerActions()

  // Live-tick while running (1s).
  useEffect(() => {
    if (!state.running) return
    const id = setInterval(() => setTick(Date.now()), 1000)
    return () => clearInterval(id)
  }, [state.running])

  // Pull the work-tree only when the picker is open.
  const { data: workTree = [] } = useQuery({
    queryKey: ['time', 'work-tree', state.scope],
    queryFn:  () => api.get<WorkTreeCustomer[]>(`/time/work-tree?scope=${state.scope}`),
    enabled:  pickerOpen,
  })

  const setScope = useCallback((scope: 'mine' | 'all') => {
    setState(prev => ({ ...prev, scope }))
  }, [setState])

  const handlePick = useCallback((task: TaskRef) => {
    startTask(task)
    setTick(Date.now())
    setPickerOpen(false)
  }, [startTask])

  const running = state.running
  const elapsed = running ? tick - running.startedAt : 0

  return (
    <>
      {running ? (
        <div className="dt-bar dt-bar-running">
          <div className="dt-pulse" />
          <div className="dt-task">
            <div className="dt-task-name">
              <span className="dt-task-customer">{running.task.customerName}</span>
              <span className="muted"> · </span>
              <span>{running.task.projectName}</span>
              <span className="muted"> · {running.task.activityName}</span>
            </div>
            <div className="dt-task-since">
              Since {new Date(running.startedAt).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })}
            </div>
          </div>
          <div className="dt-elapsed mono tabular">{fmtDur(elapsed)}</div>
          <button className="btn btn-ghost btn-icon dt-switch" onClick={() => setPickerOpen(true)} title="Switch task">
            <RefreshCcw size={14} />
          </button>
          <button className="btn dt-stop" onClick={stopRunning}>
            <Square size={12} fill="currentColor" />
            Stop
          </button>
        </div>
      ) : (
        <div className="dt-bar dt-bar-idle">
          <Clock size={14} className="dt-bar-icon" />
          <span className="dt-bar-label">No timer running</span>
          <span style={{ flex: 1 }} />
          <button className="btn btn-brand dt-start" onClick={() => setPickerOpen(true)}>
            <Play size={12} fill="currentColor" />
            Start timer
          </button>
        </div>
      )}

      {pickerOpen && (
        <PickerSheet
          open={pickerOpen}
          tree={workTree}
          scope={state.scope}
          setScope={setScope}
          currentTask={running?.task}
          onClose={() => setPickerOpen(false)}
          onPick={handlePick}
        />
      )}
    </>
  )
}
